import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRoles } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const PLACEMENTS = ["ideas_sidebar", "homepage_footer", "insights_inline", "opportunities_top"];
const TIERS = ["community", "catalyst", "anchor"];

export default function AdminSponsors() {
  const { isSuperAdmin, loading } = useRoles();
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", blurb: "", logo_url: "", link_url: "", tier: "community", placements: [] as string[] });

  const { data: sponsors } = useQuery({
    queryKey: ["admin-sponsors"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("sponsors").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  if (loading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    const { error } = await (supabase as any).from("sponsors").insert(form);
    if (error) return toast.error(error.message);
    toast.success("Sponsor created");
    setForm({ name: "", blurb: "", logo_url: "", link_url: "", tier: "community", placements: [] });
    qc.invalidateQueries({ queryKey: ["admin-sponsors"] });
  };

  const toggleActive = async (s: any) => {
    await (supabase as any).from("sponsors").update({ is_active: !s.is_active }).eq("id", s.id);
    qc.invalidateQueries({ queryKey: ["admin-sponsors"] });
  };

  const remove = async (id: string) => {
    await (supabase as any).from("sponsors").delete().eq("id", id);
    qc.invalidateQueries({ queryKey: ["admin-sponsors"] });
  };

  const togglePlacement = (p: string) => {
    setForm((f) => ({ ...f, placements: f.placements.includes(p) ? f.placements.filter((x) => x !== p) : [...f.placements, p] }));
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto px-4 py-10">
        <h1 className="font-display text-3xl font-bold text-foreground mb-6">Sponsors</h1>

        <div className="rounded-xl border border-border bg-card p-6 mb-6 space-y-3">
          <h2 className="font-display font-semibold text-foreground flex items-center gap-2">
            <Plus className="h-4 w-4" /> New sponsor
          </h2>
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Textarea placeholder="Blurb" value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} />
          <Input placeholder="Logo URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
          <Input placeholder="Link URL" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} />
          <div className="flex gap-2">
            {TIERS.map((t) => (
              <button key={t} onClick={() => setForm({ ...form, tier: t })}
                className={`px-3 py-1 rounded text-xs capitalize ${form.tier === t ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {PLACEMENTS.map((p) => (
              <button key={p} onClick={() => togglePlacement(p)}
                className={`px-3 py-1 rounded text-xs ${form.placements.includes(p) ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                {p}
              </button>
            ))}
          </div>
          <Button onClick={create}>Create</Button>
        </div>

        <div className="space-y-3">
          {sponsors?.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-foreground">{s.name}</div>
                <div className="text-xs text-muted-foreground">
                  {s.tier} · {(s.placements || []).join(", ") || "no placements"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={s.is_active ? "default" : "outline"}>{s.is_active ? "Active" : "Paused"}</Badge>
                <Button size="sm" variant="ghost" onClick={() => toggleActive(s)}>
                  {s.is_active ? "Pause" : "Activate"}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(s.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}